import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, ExternalLinkIcon, ClockIcon, TicketIcon, InfoIcon, XIcon } from 'lucide-react';
interface Show {
  id: number;
  date: string;
  time: string;
  venue: string;
  location: string;
  ticketUrl: string;
  status: 'upcoming' | 'sold-out' | 'past';
  description?: string;
  image?: string;
}
const Live = () => {
  const [selectedShow, setSelectedShow] = useState<Show | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'upcoming' | 'past'>('upcoming');
  const liveShows: Show[] = [{
    id: 1,
    date: '2023-12-15',
    time: '20:00',
    venue: 'Cosmic Lounge',
    location: 'London, UK',
    ticketUrl: '#',
    status: 'upcoming',
    description: "Join us for an intimate night of cosmic sounds at London's premier venue for alternative music. We'll be performing tracks from our debut EP plus previewing new material.",
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ps3k5nidS8uq9sbbp91Kye/1659642841402_filtered.jpg'
  }, {
    id: 2,
    date: '2024-01-20',
    time: '19:30',
    venue: 'Stellar Arena',
    location: 'Manchester, UK',
    ticketUrl: '#',
    status: 'upcoming',
    description: "Our biggest show to date! We're bringing a full audiovisual experience to Manchester's Stellar Arena with special guests to be announced."
  }, {
    id: 3,
    date: '2024-02-05',
    time: '21:00',
    venue: 'Nebula Club',
    location: 'Dublin, Ireland',
    ticketUrl: '#',
    status: 'upcoming',
    description: 'Our first Irish show promises to be an unforgettable night of interstellar rock music. Limited capacity venue - tickets expected to sell quickly!',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rfd1JqkjnJPrxrGGi1bcdH/1659642927638_filtered.jpg'
  }, {
    id: 4,
    date: '2023-09-12',
    time: '20:30',
    venue: 'Quantum Theatre',
    location: 'Bristol, UK',
    ticketUrl: '#',
    status: 'past',
    description: 'A sold-out show that featured our first live performance of "Sunchildren" and a surprise collaboration with local electronic artists.'
  }, {
    id: 5,
    date: '2023-08-05',
    time: '18:00',
    venue: 'Orbit Festival',
    location: 'Glasgow, Scotland',
    ticketUrl: '#',
    status: 'past',
    description: 'Our festival debut at Orbit 2023, where we performed alongside some of the biggest names in alternative and electronic music.'
  }, {
    id: 6,
    date: '2024-03-15',
    time: '20:00',
    venue: 'Astral Hall',
    location: 'Berlin, Germany',
    ticketUrl: '#',
    status: 'upcoming',
    description: "Our first European date of 2024! We're excited to bring our immersive show to Berlin's iconic Astral Hall."
  }];
  const filteredShows = activeFilter === 'all' ? liveShows : liveShows.filter(show => activeFilter === 'upcoming' ? show.status !== 'past' : show.status === 'past');
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const openShowDetails = (show: Show) => {
    setSelectedShow(show);
  };
  return <section className="pt-28 md:pt-36 pb-16" data-id="element-411">
      <div className="max-w-5xl mx-auto" data-id="element-412">
        <h2 className="text-5xl font-bold mb-12 font-mono text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400 inline-block" data-id="element-413">
          LIVE SHOWS
        </h2>
        {/* Featured image - Adding new section to showcase the live performance image */}
        <div className="mb-12 relative overflow-hidden rounded-lg" data-id="element-414">
          <div className="absolute -inset-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-xl blur-lg" data-id="element-415"></div>
          <div className="relative aspect-video overflow-hidden rounded-lg" data-id="element-416">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rfd1JqkjnJPrxrGGi1bcdH/1659642927638_filtered.jpg" alt="Fabric Reality live performance" className="w-full h-full object-cover" data-id="element-417" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-418"></div>
            <div className="absolute bottom-4 left-4 right-4" data-id="element-419">
              <h3 className="text-2xl font-bold text-white" data-id="element-420">
                Experience Our Live Energy
              </h3>
              <p className="text-gray-300" data-id="element-421">
                Immerse yourself in the cosmic soundscapes of Fabric Reality
              </p>
            </div>
          </div>
        </div>
        <div className="mb-8 flex flex-wrap gap-3" data-id="element-422">
          <button onClick={() => setActiveFilter('upcoming')} className={`px-4 py-2 rounded-lg transition-colors ${activeFilter === 'upcoming' ? 'bg-pink-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-423">
            Upcoming Shows
          </button>
          <button onClick={() => setActiveFilter('past')} className={`px-4 py-2 rounded-lg transition-colors ${activeFilter === 'past' ? 'bg-pink-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-424">
            Past Shows
          </button>
          <button onClick={() => setActiveFilter('all')} className={`px-4 py-2 rounded-lg transition-colors ${activeFilter === 'all' ? 'bg-pink-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-425">
            All Shows
          </button>
        </div>
        <div className="space-y-6" data-id="element-426">
          {filteredShows.length > 0 ? filteredShows.map(show => <div key={show.id} className="bg-black/60 backdrop-blur-sm p-6 rounded-lg border border-indigo-500/30 shadow-lg shadow-indigo-500/20 hover:border-pink-500/50 transition-all duration-300 transform hover:scale-[1.01]" data-id="element-427">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-428">
                  <div className="flex items-start space-x-4" data-id="element-429">
                    <div className="bg-pink-900/50 p-3 rounded-lg text-center min-w-[70px]" data-id="element-430">
                      <CalendarIcon size={24} className="text-pink-300 mx-auto mb-1" data-id="element-431" />
                      <p className="text-white font-semibold" data-id="element-432">
                        {new Date(show.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                      </p>
                      <p className="text-gray-400 text-sm" data-id="element-433">
                        {new Date(show.date).getFullYear()}
                      </p>
                    </div>
                    <div data-id="element-434">
                      <h3 className="text-xl font-semibold text-white" data-id="element-435">
                        {show.venue}
                      </h3>
                      <div className="flex items-center text-gray-300 mt-1" data-id="element-436">
                        <MapPinIcon size={16} className="mr-1" data-id="element-437" />
                        <span data-id="element-438">{show.location}</span>
                      </div>
                      <div className="flex items-center text-gray-300 mt-1" data-id="element-439">
                        <ClockIcon size={16} className="mr-1" data-id="element-440" />
                        <span data-id="element-441">{show.time}</span>
                      </div>
                      {show.status === 'sold-out' && <span className="inline-block mt-2 px-2 py-1 bg-red-900/50 text-red-300 text-xs font-semibold rounded" data-id="element-442">
                          SOLD OUT
                        </span>}
                      {show.status === 'past' && <span className="inline-block mt-2 px-2 py-1 bg-gray-800 text-gray-400 text-xs font-semibold rounded" data-id="element-443">
                          PAST EVENT
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3" data-id="element-444">
                    <button onClick={() => openShowDetails(show)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center" data-id="element-445">
                      <InfoIcon size={18} className="mr-2" data-id="element-446" />
                      Details
                    </button>
                    {show.status !== 'past' && <a href={show.ticketUrl} className={`inline-flex items-center justify-center px-6 py-3 ${show.status === 'sold-out' ? 'bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 hover:shadow-lg hover:shadow-pink-500/30'} text-white font-medium rounded-lg transition-all duration-300`} onClick={e => show.status === 'sold-out' && e.preventDefault()} data-id="element-447">
                        <TicketIcon size={18} className="mr-2" data-id="element-448" />
                        {show.status === 'sold-out' ? 'SOLD OUT' : 'TICKETS'}
                      </a>}
                  </div>
                </div>
              </div>) : <div className="bg-black/60 backdrop-blur-sm p-8 rounded-lg border border-indigo-500/30 shadow-lg shadow-indigo-500/20 text-center" data-id="element-449">
              <h3 className="text-2xl font-semibold text-white mb-2" data-id="element-450">
                No {activeFilter !== 'all' ? activeFilter : ''} shows found
              </h3>
              <p className="text-gray-300" data-id="element-451">
                Check back soon for new tour dates
              </p>
            </div>}
        </div>
        {/* Show Details Modal */}
        {selectedShow && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm" data-id="element-452">
            <div className="bg-gray-900 rounded-lg p-6 max-w-2xl w-full relative border border-pink-500/30" data-id="element-453">
              <button onClick={() => setSelectedShow(null)} className="absolute top-4 right-4 p-2 rounded-full bg-gray-800 hover:bg-gray-700 text-white transition-colors" data-id="element-454">
                <XIcon size={20} data-id="element-455" />
              </button>
              <div className="mb-6" data-id="element-456">
                <h3 className="text-2xl font-bold text-white mb-1" data-id="element-457">
                  {selectedShow.venue}
                </h3>
                <p className="text-pink-400" data-id="element-458">
                  {formatDate(selectedShow.date)} • {selectedShow.time}
                </p>
              </div>
              <div className="mb-6" data-id="element-459">
                <div className="flex items-center text-gray-300 mb-3" data-id="element-460">
                  <MapPinIcon size={18} className="mr-2 text-pink-400" data-id="element-461" />
                  <span data-id="element-462">{selectedShow.location}</span>
                </div>
                <p className="text-gray-300 mb-4" data-id="element-463">{selectedShow.description}</p>
                {selectedShow.status === 'sold-out' && <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 mb-4" data-id="element-464">
                    <p className="text-red-300" data-id="element-465">
                      This show is sold out. Join our newsletter to get early
                      access to future shows.
                    </p>
                  </div>}
                {selectedShow.status === 'past' && <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 mb-4" data-id="element-466">
                    <p className="text-gray-300" data-id="element-467">
                      This show has already taken place. Check out our upcoming
                      shows for future dates.
                    </p>
                  </div>}
              </div>
              {selectedShow.status !== 'past' && <div className="flex justify-end" data-id="element-468">
                  <a href={selectedShow.ticketUrl} className={`inline-flex items-center justify-center px-6 py-3 ${selectedShow.status === 'sold-out' ? 'bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500'} text-white font-medium rounded-lg transition-all duration-300`} onClick={e => selectedShow.status === 'sold-out' && e.preventDefault()} data-id="element-469">
                    {selectedShow.status === 'sold-out' ? 'SOLD OUT' : 'GET TICKETS'}
                    {selectedShow.status !== 'sold-out' && <ExternalLinkIcon size={18} className="ml-2" data-id="element-470" />}
                  </a>
                </div>}
            </div>
          </div>}
        <div className="mt-12 bg-black/40 backdrop-blur-sm p-6 rounded-lg border border-indigo-500/10" data-id="element-471">
          <h3 className="text-xl font-semibold text-white mb-4" data-id="element-472">
            Tour Information
          </h3>
          <p className="text-gray-300 mb-4" data-id="element-473">
            Experience Fabric Reality live for a truly immersive journey into
            our interstellar sound. Our shows feature stunning visuals, cosmic
            lighting, and an unforgettable audio experience.
          </p>
          <p className="text-gray-300" data-id="element-474">
            For booking inquiries or to bring Fabric Reality to your venue,
            please contact us at{' '}
            <a href="mailto:booking@fabricreality.com" className="text-pink-400 hover:underline" data-id="element-475">
              booking@fabricreality.com
            </a>
          </p>
        </div>
      </div>
    </section>;
};
export default Live;